'use client';

// Page d'erreur GLOBALE Next.js : affichee quand le root layout lui-meme crash.
// Doit inclure <html> et <body> car remplace tout l'arbre. Pas de motion/framer ici
// pour eviter les dependances qui pourraient elles-memes crasher (resilience max).
// Multilingue, design coherent avec error.tsx mais sans dependances externes.

import { useEffect, useState } from 'react';

type Lang = 'fr' | 'en' | 'es' | 'de' | 'it' | 'pt' | 'nl' | 'pl' | 'ar';

const t: Record<Lang, {
  title: string;
  subtitle: string;
  description: string;
  reload: string;
  rtl?: boolean;
}> = {
  fr: {
    title: 'Une virgule a fait des siennes',
    subtitle: "Quelque chose s'est cassé. C'est nous, pas vous.",
    description: 'Notre équipe est notifiée automatiquement. Réessayez dans un instant.',
    reload: 'Recharger',
  },
  en: {
    title: 'A semicolon went rogue',
    subtitle: 'Something broke. On our side, not yours.',
    description: 'Our team is notified automatically. Give it a moment and try again.',
    reload: 'Reload',
  },
  es: {
    title: 'Una coma se rebeló',
    subtitle: 'Algo se rompió. Es cosa nuestra, no tuya.',
    description: 'Nuestro equipo ha sido notificado automáticamente. Inténtalo de nuevo en un momento.',
    reload: 'Recargar',
  },
  de: {
    title: 'Ein Semikolon ist außer Kontrolle',
    subtitle: 'Etwas ist schiefgelaufen. Bei uns, nicht bei Ihnen.',
    description: 'Unser Team wird automatisch benachrichtigt. Versuchen Sie es gleich noch einmal.',
    reload: 'Neu laden',
  },
  it: {
    title: 'Una virgola si è ribellata',
    subtitle: 'Qualcosa si è rotto. Colpa nostra, non tua.',
    description: 'Il nostro team è stato avvisato automaticamente. Riprova tra un istante.',
    reload: 'Ricarica',
  },
  pt: {
    title: 'Uma vírgula saiu do controlo',
    subtitle: 'Algo correu mal. Connosco, não consigo.',
    description: 'A nossa equipa foi notificada automaticamente. Tente novamente daqui a um momento.',
    reload: 'Recarregar',
  },
  nl: {
    title: 'Een puntkomma is op hol geslagen',
    subtitle: 'Er ging iets mis. Bij ons, niet bij u.',
    description: 'Ons team is automatisch op de hoogte. Probeer het zo opnieuw.',
    reload: 'Opnieuw laden',
  },
  pl: {
    title: 'Średnik wymknął się spod kontroli',
    subtitle: 'Coś się zepsuło. Po naszej stronie, nie Twojej.',
    description: 'Nasz zespół został automatycznie powiadomiony. Spróbuj ponownie za chwilę.',
    reload: 'Odśwież',
  },
  ar: {
    title: 'فاصلة منقوطة خرجت عن السيطرة',
    subtitle: 'حدث خطأ ما. من جهتنا، لا من جهتكم.',
    description: 'تم إخطار فريقنا تلقائياً. حاول مرة أخرى بعد لحظات.',
    reload: 'إعادة التحميل',
    rtl: true,
  },
};

function detectLang(): Lang {
  if (typeof navigator === 'undefined') return 'fr';
  const lang = (navigator.language || 'fr').toLowerCase().split('-')[0];
  if (lang in t) return lang as Lang;
  return 'fr';
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [lang, setLang] = useState<Lang>('fr');
  const tr = t[lang];

  useEffect(() => {
    setLang(detectLang());

    // ChunkLoadError (PWA/onglet en cache après un déploiement) : on recharge
    // UNE fois automatiquement pour récupérer le build neuf (garde anti-boucle).
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /loading chunk|loading css chunk|dynamically imported module|failed to fetch dynamically/i.test(error?.message ?? '');
    if (isChunkError && typeof window !== 'undefined') {
      try {
        const KEY = 'vyndra-chunk-reload-at';
        const last = Number(sessionStorage.getItem(KEY) || '0');
        if (Date.now() - last > 15000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage indispo */ }
    }

    // Notification backend (best-effort)
    fetch('/api/errors/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack?.slice(0, 4000),
        digest: error.digest,
        url: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        global: true,
      }),
    }).catch(() => {});
  }, [error]);

  return (
    <html lang={lang} dir={tr.rtl ? 'rtl' : 'ltr'}>
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          // Les lumieres du fond commun (components/fonds), en ligne : cette page
          // ne doit dependre de rien. Ni flou ni animation (29/09/2026).
          background:
            'radial-gradient(44% 40% at 2% -6%, rgba(139,92,246,0.13), transparent 70%), '
            + 'radial-gradient(48% 52% at 100% 106%, rgba(34,211,238,0.12), transparent 72%), '
            + 'radial-gradient(46% 60% at 50% 58%, rgba(255,255,255,0.03), transparent 72%), '
            + '#07080a',
          color: '#E8E9EB',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 520 }}>
          {/* Logo Vyndra inline (SVG simplifie pour ne dependre de rien) */}
          <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center', position: 'relative', width: 80, marginLeft: 'auto', marginRight: 'auto' }}>
            <svg width={72} height={72} viewBox="0 0 64 64" fill="none">
              <defs>
                <linearGradient id="vlogo" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#8B5CF6" />
                  <stop offset="1" stopColor="#22D3EE" />
                </linearGradient>
              </defs>
              <rect x="4" y="4" width="56" height="56" rx="14" fill="url(#vlogo)" />
              <path d="M20 22 L32 44 L44 22" stroke="#fff" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            {/* Bulle d'alerte */}
            <span
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#EF4444',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                fontWeight: 700,
                boxShadow: '0 0 0 3px #08090B, 0 4px 12px rgba(239,68,68,0.4)',
              }}
            >
              !
            </span>
          </div>

          <h1
            style={{
              fontSize: 38,
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-1px',
              marginTop: 0,
              marginBottom: 16,
              background: 'linear-gradient(135deg, #EF4444 0%, #8B5CF6 50%, #22D3EE 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {tr.title}
          </h1>

          <p style={{ fontSize: 14, color: '#E8E9EB', marginBottom: 8, marginTop: 0, fontWeight: 500 }}>
            {tr.subtitle}
          </p>
          <p style={{ fontSize: 12.5, color: '#8A8F98', lineHeight: 1.5, marginTop: 0, marginBottom: 32 }}>
            {tr.description}
          </p>

          <button
            type="button"
            onClick={() => reset()}
            style={{
              padding: '12px 22px',
              borderRadius: 10,
              border: 'none',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C4FF4 100%)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(139,92,246,0.35)',
              transition: 'transform 150ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {tr.reload}
          </button>

          {error.digest && (
            <div
              style={{
                marginTop: 32,
                fontSize: 10,
                color: '#3A3D45',
                fontFamily: 'monospace',
                letterSpacing: 0.5,
              }}
            >
              [{error.digest}]
            </div>
          )}
        </div>
      </body>
    </html>
  );
}
